/**
 * Platform Admin API Client
 *
 * API layer for the Platform Admin portal. Uses openapi-fetch typed clients
 * for all endpoints with OpenAPI spec coverage:
 * - platform-admin spec (tenants, integrations, data providers)
 * - audit-trail spec (audit logs, integrity verification)
 * - property-title-admin spec (property valuations, title searches, ATTOM testing)
 *
 * Typed clients enforce compile-time safety: tsc catches field name mismatches
 * that raw fetch().json() would silently pass as `any`.
 *
 * Zero legacyFetch calls remaining. All endpoints fully typed.
 */

import type { components } from '@aaimplatform/institutional-types/platform-admin';
import {
  platformAdminClient,
  platformAdminRootClient,
  auditTrailClient,
  propertyTitleAdminClient,
  regulatoryReadinessClient,
  uccFilingLifecycleClient,
  uccEntitiesClient,
  uccFilingAnalyticsClient,
  uccBulkOperationsClient,
  uccFilingAuditComplianceClient,
  uccMultiStateFilingClient,
  uccFilingWebhooksClient,
  uccJurisdictionRulesClient,
  uccFilingSubmissionClient,
  uccPaperFilingClient,
  uccSearchClient,
  uccFeeReconciliationClient,
  loanSyndicationClient,
  cooperativeReserveTokenClient,
  identityProviderClient,
  supportPlatformClient,
  dataBrowserClient,
  recoveryCaseAdminClient,
} from './typed-clients';
import type { PropertyTitleAdmin } from '@aaimplatform/institutional-types';
import type { AuditTrail } from '@aaimplatform/institutional-types';
import { useAuthStore } from '../stores/auth';
import { apiErrorMessage } from './error-message';
import { readCsrfCookie } from '../lib/csrf';

// ---------------------------------------------------------------------------
// Auth middleware -- inject Bearer token into all authenticated client requests
// ---------------------------------------------------------------------------

/**
 * Detect responses that indicate the user's auth state is broken:
 * - 401 (no/invalid Bearer)
 * - 400 with `code: 'TENANT_SCOPE_REQUIRED'` (JWT decoded but missing tenantId claim,
 *   typically a stale token from before a schema change or a partial dev session)
 * - 400 with `code: 'AUTH_REQUIRED'` (server rejected before tenant context lookup)
 *
 * Sprint C10 follow-on: the BYPASS_AUTH SPA short-circuit lets users land on protected
 * pages without a real auth state. When the platform-admin-api dev-bypass injector
 * fails to fire (because req.user got set from a partial JWT), tenant scope rejects
 * with 400. The user must be bounced to /login to clear the partial state and re-auth.
 */
async function isAuthBrokenResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status === 400) {
    try {
      const body = (await response.clone().json()) as
        | { error?: { code?: string }; code?: string }
        | undefined;
      const code = body?.error?.code ?? body?.code;
      return code === 'TENANT_SCOPE_REQUIRED' || code === 'AUTH_REQUIRED';
    } catch {
      return false;
    }
  }
  return false;
}

function bounceToLogin(): void {
  // Sprint C10 follow-on: do NOT bounce while on /login or /auth/callback.
  // - /login bounce would loop trivially.
  // - /auth/callback is mid-OAuth flow; the page is about to call
  //   completeOAuthExchange() (POST /auth/exchange -> cookie + /me hydrate) and
  //   navigate to dashboard. Bouncing during the brief pre-hydration window
  //   would re-launch the OAuth flow and create an infinite redirect cycle
  //   (callback -> 401 -> bounce -> callback).
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path.startsWith('/login') || path.startsWith('/auth/callback')) {
    return;
  }
  useAuthStore.getState().logout();
  window.location.href = '/login';
}

// ---------------------------------------------------------------------------
// CSRF double-submit (N8, GH #1806) -- echo the csrf-token cookie as X-CSRF-Token
// ---------------------------------------------------------------------------

/** Methods that mutate state and therefore carry the CSRF header. */
export const CSRF_MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * readCsrfCookie reads the NON-HttpOnly `csrf-token` cookie the session issuer
 * set alongside the HttpOnly session cookie. The SPA echoes it as the
 * `X-CSRF-Token` header on mutating requests; a backend that enforces the
 * double-submit match refuses a cookie-authenticated mutation without it. It
 * returns null when the cookie is absent. It is NOT a credential (a
 * per-session CSRF nonce), so there is no no-browser-token-storage concern.
 * Defined in lib/csrf.ts so stores/auth.ts, which this module imports, reads
 * it too (GH #4888); re-exported here for the modules that import it from
 * this file.
 */
export { readCsrfCookie };

export function authMiddleware() {
  return {
    // CSRF double-submit (N8): on mutating methods, echo the non-HttpOnly
    // csrf-token cookie as X-CSRF-Token. The @aaimplatform/database auth
    // middleware compares the two for cookie-authenticated requests. Safe
    // methods (GET/HEAD/OPTIONS) carry no header. No-op when the cookie is
    // absent (pre-rollout) so this change is backward-compatible.
    onRequest({ request }: { request: Request }) {
      if (CSRF_MUTATING_METHODS.has(request.method.toUpperCase())) {
        const csrf = readCsrfCookie();
        if (csrf) request.headers.set('X-CSRF-Token', csrf);
      }
      return request;
    },
    // browser-token-storage-hardening: no Authorization header is injected. The
    // session rides the HttpOnly `auth-token` cookie (the @aaimplatform/database
    // cookie->Bearer shim reads it server-side); the factory in
    // @aaimplatform/institutional-types/api-clients defaults every client to
    // credentials:'include' so the cookie is attached. The SPA holds no token.
    async onResponse({ response }: { response: Response }) {
      if (await isAuthBrokenResponse(response)) {
        bounceToLogin();
      }
      return response;
    },
  };
}

platformAdminClient.use(authMiddleware());
platformAdminRootClient.use(authMiddleware());
auditTrailClient.use(authMiddleware());
propertyTitleAdminClient.use(authMiddleware());
regulatoryReadinessClient.use(authMiddleware());
uccFilingLifecycleClient.use(authMiddleware());
uccEntitiesClient.use(authMiddleware());
uccFilingAnalyticsClient.use(authMiddleware());
uccBulkOperationsClient.use(authMiddleware());
uccFilingAuditComplianceClient.use(authMiddleware());
uccMultiStateFilingClient.use(authMiddleware());
uccFilingWebhooksClient.use(authMiddleware());
uccJurisdictionRulesClient.use(authMiddleware());
uccFilingSubmissionClient.use(authMiddleware());
uccPaperFilingClient.use(authMiddleware());
uccSearchClient.use(authMiddleware());
uccFeeReconciliationClient.use(authMiddleware());
loanSyndicationClient.use(authMiddleware());
cooperativeReserveTokenClient.use(authMiddleware());
identityProviderClient.use(authMiddleware());
supportPlatformClient.use(authMiddleware());
dataBrowserClient.use(authMiddleware());
recoveryCaseAdminClient.use(authMiddleware());

// ---------------------------------------------------------------------------
// Generic legacy fetch wrapper for non-spec'd endpoints
//
// F-PORTAL-AUDIENCE-SPLIT (2026-05-17): the platform-config pages migrated
// from admin-portal use this wrapper for endpoints that are not yet on the
// platform-admin OpenAPI surface (api keys, regenerated secrets, raw
// platform-config domain GET/PATCH). Prefer typed clients
// (platformAdminClient, etc.) for spec'd paths -- see
// .claude/rules/typescript-compliance.md.
// ---------------------------------------------------------------------------

const APEX_TENANT_ID = '00000000-0000-0000-0000-000000aaaaaa';

export const apiClient = {
  async get<T>(endpoint: string): Promise<T> {
    return legacyRequest<T>(endpoint, { method: 'GET' });
  },
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return legacyRequest<T>(endpoint, {
      method: 'POST',
      body: data === undefined ? undefined : JSON.stringify(data),
    });
  },
  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return legacyRequest<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return legacyRequest<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  },
  async delete<T>(endpoint: string): Promise<T> {
    return legacyRequest<T>(endpoint, { method: 'DELETE' });
  },
};

async function legacyRequest<T>(endpoint: string, init: RequestInit): Promise<T> {
  const state = useAuthStore.getState();
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  // browser-token-storage-hardening: auth rides the HttpOnly cookie
  // (credentials:'include'); no Bearer token is read from the store.
  const tenantId = state.user?.tenantId ?? (state.user ? APEX_TENANT_ID : null);
  if (tenantId) {
    headers.set('X-Tenant-ID', tenantId);
  }
  // CSRF double-submit (N8): echo the csrf-token cookie as X-CSRF-Token on
  // mutating methods, mirroring the openapi-fetch authMiddleware.onRequest.
  const method = (init.method ?? 'GET').toUpperCase();
  if (CSRF_MUTATING_METHODS.has(method)) {
    const csrf = readCsrfCookie();
    if (csrf) headers.set('X-CSRF-Token', csrf);
  }
  const url = endpoint.startsWith('/api') ? endpoint : `/api${endpoint}`;
  const response = await fetch(url, { ...init, headers, credentials: 'include' });
  if (!response.ok) {
    if (await isAuthBrokenResponse(response)) {
      bounceToLogin();
    }
    const errorBody = (await response.json().catch(() => null)) as
      | { message?: string }
      | null;
    throw new Error(
      apiErrorMessage(errorBody) ?? `Request failed (${response.status})`,
    );
  }
  if (response.status === 204) return null as T;
  return response.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Re-export generated types for consumer convenience
// ---------------------------------------------------------------------------

export type Integration = components['schemas']['Integration'];
export type IntegrationTestResult = components['schemas']['IntegrationTestResult'];
export type Tenant = components['schemas']['Tenant'];
export type TenantDetail = components['schemas']['TenantDetail'];
export type TenantStatus = components['schemas']['TenantStatus'];
export type TenantType = components['schemas']['InstitutionType'];
export type TenantListResponse = components['schemas']['TenantListResponse'];
export type CreateTenantRequest = components['schemas']['CreateTenantRequest'];
export type UpdateTenantRequest = components['schemas']['UpdateTenantRequest'];
export type TenantPALSettings = components['schemas']['TenantPALSettings'];
export type TenantStats = components['schemas']['TenantStats'];
export type TenantIntegration = components['schemas']['TenantIntegration'];
export type PlatformSettings = components['schemas']['PlatformSettings'];
export type FeatureFlag = components['schemas']['FeatureFlag'];
export type SystemHealth = components['schemas']['SystemHealth'];
export type ServiceHealth = components['schemas']['ServiceHealth'];
export type SystemMetrics = components['schemas']['SystemMetrics'];
export type SystemHealthDashboard = components['schemas']['SystemHealthDashboard'];
export type ServiceStatusEntry = components['schemas']['ServiceStatusEntry'];
export type IntegrationHealthStatus = components['schemas']['IntegrationHealthStatus'];
export type PlatformDashboardStats = components['schemas']['PlatformDashboardStats'];

// Audit Trail type aliases from generated OpenAPI types
export type AuditLogEntryResponse = AuditTrail.components['schemas']['AuditLogEntryResponse'];
export type AuditLogEntryInput = AuditTrail.components['schemas']['AuditLogEntryInput'];
export type AuditActionType = AuditTrail.components['schemas']['AuditActionType'];
export type AuditLogQueryResponse = AuditTrail.components['schemas']['AuditLogQueryResponse'];
export type AuditLogIntegrityResponse = AuditTrail.components['schemas']['AuditLogIntegrityResponse'];

// ---------------------------------------------------------------------------
// Property, title, and ATTOM type aliases from generated OpenAPI types
// Migrated to property-title-admin spec as of Sprint 043
// ---------------------------------------------------------------------------

export type ValuationStatus = PropertyTitleAdmin.components['schemas']['ValuationStatus'];
export type TitleSearchStatus = PropertyTitleAdmin.components['schemas']['TitleSearchStatus'];
export type PropertyAddress = PropertyTitleAdmin.components['schemas']['PropertyAddress'];
export type PropertyValuation = PropertyTitleAdmin.components['schemas']['PropertyValuation'];
export type PropertyValuationRequest = PropertyTitleAdmin.components['schemas']['PropertyValuationRequest'];
export type PropertyValuationListResponse = PropertyTitleAdmin.components['schemas']['PropertyValuationListResponse'];
export type TitleSearch = PropertyTitleAdmin.components['schemas']['TitleSearch'];
export type TitleSearchRequest = PropertyTitleAdmin.components['schemas']['TitleSearchRequest'];
export type TitleSearchListResponse = PropertyTitleAdmin.components['schemas']['TitleSearchListResponse'];
export type LienVerificationRequest = PropertyTitleAdmin.components['schemas']['LienVerificationRequest'];
export type LienVerificationResult = PropertyTitleAdmin.components['schemas']['LienVerificationResult'];
export type EquityCalculationRequest = PropertyTitleAdmin.components['schemas']['EquityCalculationRequest'];
export type EquityCalculationResult = PropertyTitleAdmin.components['schemas']['EquityCalculationResult'];
export type PropertyProvider = PropertyTitleAdmin.components['schemas']['PropertyProvider'];
export type PropertyProviderStatusResponse = PropertyTitleAdmin.components['schemas']['PropertyProviderStatusResponse'];
export type AttomAddress = PropertyTitleAdmin.components['schemas']['AttomAddress'];
export type AttomPropertyProfile = PropertyTitleAdmin.components['schemas']['AttomPropertyProfile'];
export type AttomHealthStatus = PropertyTitleAdmin.components['schemas']['AttomHealthStatus'];
export type AttomTestLookupRequest = PropertyTitleAdmin.components['schemas']['AttomTestLookupRequest'];
export type AttomTestLookupResponse = PropertyTitleAdmin.components['schemas']['AttomTestLookupResponse'];
export type SampleAddress = PropertyTitleAdmin.components['schemas']['SampleAddress'];
// Audit statistics type from generated OpenAPI types
export type AuditLogStatistics = AuditTrail.components['schemas']['AuditLogStatistics'];
// ---------------------------------------------------------------------------
// Shared error handling for typed client responses
// ---------------------------------------------------------------------------

export { apiErrorMessage } from './error-message';

/**
 * Extract data from an openapi-fetch response, throwing on error.
 * Preserves the same error-handling contract as the former apiFetch wrapper.
 */
function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    throw new Error(apiErrorMessage(result.error) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

// =============================================================================
// Tenants API (typed -- platform-admin spec)
// =============================================================================

export const tenantsApi = {
  /** List all tenants with optional filtering */
  list: async (params?: {
    search?: string;
    status?: TenantStatus;
    type?: TenantType;
  }): Promise<TenantListResponse> => {
    const result = await platformAdminClient.GET('/tenants', {
      params: {
        query: {
          search: params?.search,
          status: params?.status,
          type: params?.type,
        },
      },
    });
    return unwrap(result);
  },

  /** Get a single tenant by ID */
  get: async (tenantId: string): Promise<TenantDetail> => {
    const result = await platformAdminClient.GET('/tenants/{tenantId}', {
      params: { path: { tenantId } },
    });
    return unwrap(result);
  },

  /** Create (onboard) a new tenant */
  create: async (body: CreateTenantRequest): Promise<TenantDetail> => {
    const result = await platformAdminClient.POST('/tenants', {
      body,
    });
    return unwrap(result);
  },

  /**
   * Edit a tenant's organization profile (PATCH /tenants/{tenantId},
   * UpdateTenantRequest). TenantDetail sends the operator's primary contact
   * edit through it (GH #4155).
   */
  update: async (tenantId: string, body: UpdateTenantRequest): Promise<Tenant> => {
    const result = await platformAdminClient.PATCH('/tenants/{tenantId}', {
      params: { path: { tenantId } },
      body,
    });
    return unwrap(result);
  },

  delete: async (tenantId: string): Promise<void> => {
    const result = await platformAdminClient.DELETE('/tenants/{tenantId}', {
      params: { path: { tenantId } },
    });
    // Spec defines only 204 success; any non-2xx comes through as a non-ok response.
    if (!result.response.ok) {
      throw new Error(`Failed to delete tenant: HTTP ${result.response.status}`);
    }
  },
};

// =============================================================================
// Asset Classes API (typed -- platform-admin spec, Sprint C15-C WP E-3)
// =============================================================================

export type AssetClass = components['schemas']['AssetClass'];
export type AssetClassCreate = components['schemas']['AssetClassCreate'];
export type AssetClassUpdate = components['schemas']['AssetClassUpdate'];
export type AssetClassListResponse = components['schemas']['AssetClassListResponse'];
export type RegulatoryPerfectionMethod = components['schemas']['RegulatoryPerfectionMethod'];

export const assetClassesApi = {
  /** List asset class definitions, optionally filtered by group / UCC class / search. */
  list: async (params?: {
    groupCode?: string;
    uccClassification?: string;
    search?: string;
  }): Promise<AssetClassListResponse> => {
    const result = await platformAdminClient.GET('/asset-classes', {
      params: {
        query: {
          groupCode: params?.groupCode,
          uccClassification: params?.uccClassification,
          search: params?.search,
        },
      },
    });
    return unwrap(result);
  },

  /** Get a single asset class by its unique business key. */
  get: async (assetClassCode: string): Promise<AssetClass> => {
    const result = await platformAdminClient.GET(
      '/asset-classes/{assetClassCode}',
      { params: { path: { assetClassCode } } },
    );
    return unwrap(result);
  },

  /** Create a new asset class definition. */
  create: async (body: AssetClassCreate): Promise<AssetClass> => {
    const result = await platformAdminClient.POST('/asset-classes', {
      body,
    });
    return unwrap(result);
  },

  /** Update an existing asset class definition (partial). */
  update: async (
    assetClassCode: string,
    body: AssetClassUpdate,
  ): Promise<AssetClass> => {
    const result = await platformAdminClient.PUT(
      '/asset-classes/{assetClassCode}',
      { params: { path: { assetClassCode } }, body },
    );
    return unwrap(result);
  },

  /** Hard-delete an asset class definition (rejected if dependents exist). */
  delete: async (assetClassCode: string): Promise<void> => {
    const result = await platformAdminClient.DELETE(
      '/asset-classes/{assetClassCode}',
      { params: { path: { assetClassCode } } },
    );
    if (!result.response.ok) {
      throw new Error(
        `Failed to delete asset class: HTTP ${result.response.status}`,
      );
    }
  },
};

// =============================================================================
// Platform Settings API (typed -- platform-admin spec)
// =============================================================================

export const settingsApi = {
  get: async (): Promise<Record<string, unknown>> => {
    const result = await platformAdminClient.GET('/settings');
    return unwrap(result) as Record<string, unknown>;
  },

  update: async (settings: Record<string, unknown>): Promise<Record<string, unknown>> => {
    const result = await platformAdminClient.PATCH('/settings', {
      body: settings as components['schemas']['UpdatePlatformSettingsRequest'],
    });
    return unwrap(result) as Record<string, unknown>;
  },
};

// =============================================================================
// Notifications API (typed -- platform-admin spec)
// =============================================================================

export type NotificationEvent = components['schemas']['NotificationEvent'];

export const notificationsApi = {
  /**
   * Fetch the most recent operator notifications. Backed by the
   * platform-admin spec endpoint `/notifications/recent` (see
   * schema-registry/platform-admin.openapi.yaml line 2160).
   *
   * Sprint C12 Lane 4: replaced the prior hardcoded `[]` with the typed
   * client call so the operator console populates from real data.
   */
  getRecent: async (limit?: number): Promise<NotificationEvent[]> => {
    const { data, error } = await platformAdminClient.GET('/notifications/recent', {
      params: { query: { limit } },
    });
    if (error) {
      throw new Error(`Failed to fetch notifications: ${JSON.stringify(error)}`);
    }
    return data?.notifications ?? [];
  },
};

// =============================================================================
// Integrations API (typed -- platform-admin spec)
// =============================================================================

export const integrationsApi = {
  /** List all integrations (mixed: third-party integrations + Aaim services) */
  list: async (): Promise<Integration[]> => {
    const result = await platformAdminClient.GET('/integrations');
    return unwrap(result);
  },

  /** List third-party (non-Aaim) integrations -- backs the Integrations page */
  listThirdParty: async (): Promise<Integration[]> => {
    const result = await platformAdminClient.GET('/integrations/third-party');
    return unwrap(result);
  },

  /** List internal Aaim service integrations -- backs the Aaim Services page (Sprint C17) */
  listAaimServices: async (): Promise<Integration[]> => {
    const result = await platformAdminClient.GET('/integrations/aaim-services');
    return unwrap(result);
  },

  /** Test integration connection */
  testConnection: async (integrationId: string): Promise<{ success: boolean; latencyMs: number; error?: string; details?: Record<string, unknown> }> => {
    const result = await platformAdminClient.POST('/integrations/{integrationId}/test', {
      params: { path: { integrationId } },
    });
    return unwrap(result);
  },

  /** Update integration (enable/disable, config) */
  update: async (integrationId: string, body: { enabled?: boolean; config?: Record<string, never> }): Promise<Integration> => {
    const result = await platformAdminClient.PATCH('/integrations/{integrationId}', {
      params: { path: { integrationId } },
      body,
    });
    return unwrap(result);
  },
};

// =============================================================================
// Audit Logs API (typed -- audit-trail spec)
// =============================================================================

/** Filter parameters for audit log list requests */
export interface AuditLogFilters {
  tenantId?: string;
  userId?: string;
  action?: AuditActionType;
  resourceType?: string;
  resourceId?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
  sortBy?: 'timestamp' | 'action' | 'resourceType' | 'userId';
  sortOrder?: 'asc' | 'desc';
}

export const auditLogsApi = {
  /** List audit logs with filtering and pagination */
  list: async (filters?: AuditLogFilters): Promise<AuditLogQueryResponse> => {
    const result = await auditTrailClient.GET('/audit/logs', {
      params: {
        query: {
          tenantId: filters?.tenantId,
          userId: filters?.userId,
          action: filters?.action,
          resourceType: filters?.resourceType,
          resourceId: filters?.resourceId,
          startDate: filters?.startDate,
          endDate: filters?.endDate,
          limit: filters?.limit,
          offset: filters?.offset,
          sortBy: filters?.sortBy,
          sortOrder: filters?.sortOrder,
        },
      },
    });
    return unwrap(result);
  },

  /** Get a single audit log entry by ID */
  get: async (logId: string): Promise<AuditLogEntryResponse> => {
    const result = await auditTrailClient.GET('/audit/logs/{logId}', {
      params: { path: { logId } },
    });
    return unwrap(result);
  },

  /** Create a new audit log entry (for service-to-service and testing) */
  create: async (input: AuditLogEntryInput): Promise<AuditLogEntryResponse> => {
    const result = await auditTrailClient.POST('/audit/logs', {
      body: input,
    });
    return unwrap(result);
  },

  /**
   * Export audit logs as a downloadable CSV file.
   * Returns a Blob containing CSV content with proper headers.
   *
   * NOTE: This endpoint returns non-JSON content (CSV), so it cannot use
   * the typed openapi-fetch client. Raw fetch is required.
   */
  exportCsv: async (filters?: Omit<AuditLogFilters, 'limit' | 'offset'>): Promise<Blob> => {
    const searchParams = new URLSearchParams();
    if (filters?.tenantId) searchParams.set('tenantId', filters.tenantId);
    if (filters?.userId) searchParams.set('userId', filters.userId);
    if (filters?.action) searchParams.set('action', filters.action);
    if (filters?.resourceType) searchParams.set('resourceType', filters.resourceType);
    if (filters?.resourceId) searchParams.set('resourceId', filters.resourceId);
    if (filters?.startDate) searchParams.set('startDate', filters.startDate);
    if (filters?.endDate) searchParams.set('endDate', filters.endDate);
    if (filters?.sortBy) searchParams.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) searchParams.set('sortOrder', filters.sortOrder);
    const paramString = searchParams.toString();
    const url = `/api/admin/audit/logs/export${paramString ? `?${paramString}` : ''}`;

    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    return response.blob();
  },

  /** Get aggregate audit log statistics for dashboard display */
  getStatistics: async (tenantId?: string): Promise<AuditLogStatistics> => {
    const result = await auditTrailClient.GET('/audit/logs/statistics', {
      params: {
        query: { tenantId },
      },
    });
    return unwrap(result);
  },

  /** Verify the integrity of a specific audit log entry */
  verify: async (logId: string): Promise<AuditLogIntegrityResponse> => {
    const result = await auditTrailClient.POST('/audit/logs/{logId}/verify', {
      params: { path: { logId } },
    });
    return unwrap(result);
  },
};

// =============================================================================
// Property Valuations API (typed -- property-title-admin spec)
// =============================================================================

export const propertyValuationsApi = {
  /** List property valuations */
  list: async (params?: {
    status?: ValuationStatus;
    tenantId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PropertyValuationListResponse> => {
    const result = await propertyTitleAdminClient.GET('/property/valuations', {
      params: {
        query: {
          status: params?.status,
          tenantId: params?.tenantId,
          limit: params?.limit,
          offset: params?.offset,
        },
      },
    });
    return unwrap(result);
  },

  /** Request new property valuation */
  create: async (request: PropertyValuationRequest): Promise<PropertyValuation> => {
    const result = await propertyTitleAdminClient.POST('/property/valuations', {
      body: request,
    });
    return unwrap(result);
  },

  /** Calculate property equity */
  calculateEquity: async (request: EquityCalculationRequest): Promise<EquityCalculationResult> => {
    const result = await propertyTitleAdminClient.POST('/property/equity', {
      body: request,
    });
    return unwrap(result);
  },

  /** Get property data provider status */
  getProviderStatus: async (): Promise<PropertyProviderStatusResponse> => {
    const result = await propertyTitleAdminClient.GET('/property/providers/status');
    return unwrap(result);
  },
};

// =============================================================================
// Title Searches API (typed -- property-title-admin spec)
// =============================================================================

export const titleSearchesApi = {
  /** List title searches */
  list: async (params?: {
    status?: TitleSearchStatus;
    tenantId?: string;
    limit?: number;
    offset?: number;
  }): Promise<TitleSearchListResponse> => {
    const result = await propertyTitleAdminClient.GET('/title/searches', {
      params: {
        query: {
          status: params?.status,
          tenantId: params?.tenantId,
          limit: params?.limit,
          offset: params?.offset,
        },
      },
    });
    return unwrap(result);
  },

  /** Initiate new title search */
  create: async (request: TitleSearchRequest): Promise<TitleSearch> => {
    const result = await propertyTitleAdminClient.POST('/title/searches', {
      body: request,
    });
    return unwrap(result);
  },

  /** Get title search by ID */
  get: async (searchId: string): Promise<TitleSearch> => {
    const result = await propertyTitleAdminClient.GET('/title/searches/{searchId}', {
      params: { path: { searchId } },
    });
    return unwrap(result);
  },

  /** Verify lien position */
  verifyLien: async (request: LienVerificationRequest): Promise<LienVerificationResult> => {
    const result = await propertyTitleAdminClient.POST('/title/lien-verification', {
      body: request,
    });
    return unwrap(result);
  },
};

// =============================================================================
// Provider Credentials API (typed -- platform-admin spec)
// =============================================================================

export type ProviderCredential = components['schemas']['ProviderCredential'];
export type UpdateProviderCredentialRequest = components['schemas']['UpdateProviderCredentialRequest'];
export type ProviderCredentialKeyResponse = components['schemas']['ProviderCredentialKeyResponse'];

export const providerCredentialApi = {
  /** List all provider credentials (keys masked) */
  list: async (): Promise<ProviderCredential[]> => {
    const result = await platformAdminRootClient.GET('/admin/provider-credentials');
    return unwrap(result);
  },

  /** Get a single provider credential (key masked) */
  getCredential: async (provider: string): Promise<ProviderCredential> => {
    const result = await platformAdminRootClient.GET('/admin/provider-credentials/{provider}', {
      params: { path: { provider } },
    });
    return unwrap(result);
  },

  /** Update a provider API key */
  updateCredential: async (provider: string, apiKey: string): Promise<ProviderCredential> => {
    const result = await platformAdminRootClient.PUT('/admin/provider-credentials/{provider}', {
      params: { path: { provider } },
      body: { apiKey },
    });
    return unwrap(result);
  },
};

// =============================================================================
// ATTOM Property Data API -- provider credential + test-lookup + health (typed -- property-title-admin spec)
// =============================================================================

export const attomTestApi = {
  /** Get ATTOM adapter health status */
  getHealth: async (): Promise<AttomHealthStatus> => {
    const result = await propertyTitleAdminClient.GET('/property/providers/attom/health');
    return unwrap(result);
  },

  /** Validate ATTOM API key */
  validateApiKey: async () => {
    const result = await propertyTitleAdminClient.POST('/property/providers/attom/validate');
    return unwrap(result);
  },

  /** Test property lookup */
  testLookup: async (request: AttomTestLookupRequest): Promise<AttomTestLookupResponse> => {
    const result = await propertyTitleAdminClient.POST('/property/providers/attom/test/lookup', {
      body: request,
    });
    return unwrap(result);
  },

  /** Get sample addresses for testing */
  getSampleAddresses: async (): Promise<{
    sampleAddresses: SampleAddress[];
    note: string;
    timestamp: string;
  }> => {
    const result = await propertyTitleAdminClient.GET('/property/providers/attom/test/sample-addresses');
    return unwrap(result);
  },
};

// =============================================================================
// System Health API (typed -- platform-admin spec)
// =============================================================================

export const systemHealthApi = {
  /** Get aggregated system health dashboard (polls downstream services) */
  getDashboard: async (): Promise<SystemHealthDashboard> => {
    const result = await platformAdminClient.GET('/health/dashboard');
    return unwrap(result);
  },
};

// =============================================================================
// Feature Flags API (typed -- platform-admin spec)
// =============================================================================

export const featureFlagsApi = {
  /** List platform-level feature flags */
  listPlatform: async (): Promise<FeatureFlag[]> => {
    const result = await platformAdminClient.GET('/settings/feature-flags');
    return unwrap(result);
  },

  /** Update platform-level feature flags. Body is a map of flag key -> enabled boolean. */
  updatePlatform: async (updates: Record<string, boolean>): Promise<FeatureFlag[]> => {
    const result = await platformAdminClient.PATCH('/settings/feature-flags', {
      body: updates,
    });
    return unwrap(result);
  },

  /** Get tenant-scoped feature flags (merged platform + tenant overrides) */
  listTenant: async (tenantId: string): Promise<FeatureFlag[]> => {
    const result = await platformAdminClient.GET('/settings/feature-flags/{tenantId}', {
      params: { path: { tenantId } },
    });
    return unwrap(result);
  },

  /** Update tenant-scoped feature flags */
  updateTenant: async (tenantId: string, updates: Record<string, boolean>): Promise<FeatureFlag[]> => {
    const result = await platformAdminClient.PATCH('/settings/feature-flags/{tenantId}', {
      params: { path: { tenantId } },
      body: updates,
    });
    return unwrap(result);
  },
};

// =============================================================================
// Platform Dashboard Stats API (typed -- platform-admin spec)
// =============================================================================

export const dashboardStatsApi = {
  /** Get aggregated platform dashboard statistics (tenant/pledge/alert/webhook metrics) */
  getStats: async (): Promise<PlatformDashboardStats> => {
    const result = await platformAdminClient.GET('/dashboard/stats');
    return unwrap(result);
  },
};
